import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { 
  PaymentMethodsState
} from '../states/payment-methods.state';
import { PaymentMethodsManagerService } from './payment-methods-manager.service';
import { 
  EnumService, 
  LocalStorageService, 
  BankAccount, 
  CreditCard 
} from '@ops/shared-types';

@Injectable({
  providedIn: 'root'
})
export class EnhancedEnumService {
  constructor(
    private store: Store,
    private paymentMethodsManager: PaymentMethodsManagerService,
    private enumService: EnumService,
    private localStorageService: LocalStorageService
  ) {}

  /**
   * Get paid-by options with enhanced state management
   * This replaces the original getPaidByOptions method with state-aware implementation
   */
  async getPaidByOptions(
    paymentMethod: string,
    companyId: string
  ): Promise<{
    paidByList: Array<{ id: string; name: string; currencyCode?: string }>;
    optionsList: Array<string>;
  }> {
    const baseCurrencyCode = this.localStorageService.getCurrentCompany()?.baseCurrencyCode || 'JPY';
    let paidByList: Array<{ id: string; name: string; currencyCode?: string }> = [];
    let paidByOptions: Array<string | undefined> = [];

    switch (paymentMethod?.toUpperCase()) {
      case 'BANK TRANSFER': {
        // Use state management for bank accounts
        const bankAccounts = await this.paymentMethodsManager
          .getActiveBankAccounts()
          .pipe(
            map((accounts: any[]) => accounts.filter(account => account.company === companyId))
          )
          .toPromise() || [];

        paidByList = bankAccounts
          .filter(account => !account.isDebit)
          .filter(account => !['DEBIT', 'CASH', 'TEMP'].includes(account.branchName?.toUpperCase()))
          .map(account => ({
            id: account._id,
            name: account.shortName,
            currencyCode: account.currencyCode,
          }));

        paidByOptions = paidByList.map(item => item.name);
        break;
      }

      case 'CREDIT CARD': {
        // Use state management for credit cards
        const creditCards = await this.paymentMethodsManager
          .getActiveCreditCards()
          .pipe(
            map((cards: any[]) => cards.filter(card => card.company === companyId))
          )
          .toPromise() || [];

        paidByList = creditCards
          .filter(card => card.isActive)
          .map(card => ({
            id: card._id,
            name: card.shortName,
            currencyCode: card.currencyCode,
          }));

        paidByOptions = paidByList.map(item => item.name);
        break;
      }

      case 'DEBIT / CASH': {
        // Use state management for debit accounts
        const bankAccounts = await this.paymentMethodsManager
          .getActiveBankAccounts()
          .pipe(
            map((accounts: any[]) => accounts.filter(account => account.company === companyId))
          )
          .toPromise() || [];

        paidByList = bankAccounts
          .filter(account => account.isDebit)
          .map(account => ({
            id: account._id,
            name: account.shortName,
            currencyCode: account.currencyCode,
          }));

        paidByOptions = paidByList.map(item => item.name);
        break;
      }

      case 'PERSONAL MONEY': {
        // Personal money options (static)
        paidByList = [
          {
            id: 'personal',
            name: 'Personal Money',
            currencyCode: baseCurrencyCode,
          },
        ];
        paidByOptions = ['Personal Money'];
        break;
      }

      default: {
        paidByList = [];
        paidByOptions = [];
        break;
      }
    }

    return {
      paidByList: paidByList.filter(item => item.name),
      optionsList: paidByOptions.filter(option => option),
    };
  }

  /**
   * Get all paid-by options for all payment methods (enhanced version)
   */
  async getAllPaidByOptions(companyId: string): Promise<Array<{
    method: string;
    pairList: Array<{ id: string; name: string; currencyCode?: string }>;
    optionsList: Array<string>;
  }>> {
    const paymentMethods = this.enumService.getPaymentMethodOptions();
    const allOptions: Array<{
      method: string;
      pairList: Array<{ id: string; name: string; currencyCode?: string }>;
      optionsList: Array<string>;
    }> = [];

    for (const method of paymentMethods) {
      try {
        const options = await this.getPaidByOptions(method, companyId);
        allOptions.push({
          method,
          pairList: options.paidByList,
          optionsList: options.optionsList,
        });
      } catch (error) {
        console.error(`Error loading paid-by options for ${method}:`, error);
        allOptions.push({
          method,
          pairList: [],
          optionsList: [],
        });
      }
    }

    return allOptions;
  }

  /**
   * Get bank accounts for dropdown (state-aware)
   */
  getBankAccountsForDropdown(companyId: string): Observable<Array<{key: string, value: string}>> {
    return this.paymentMethodsManager.getActiveBankAccounts().pipe(
      map((accounts: any[]) => 
        accounts
          .filter(account => account.company === companyId)
          .filter(account => !account.isDebit)
          .filter(account => !['DEBIT', 'CASH', 'TEMP'].includes(account.bankName?.toUpperCase()))
          .filter(account => !['DEBIT', 'CASH', 'TEMP'].includes(account.branchName?.toUpperCase()))
          .map(account => ({
            key: account._id,
            value: account.shortName
          }))
      )
    );
  }

  /**
   * Get credit cards for dropdown (state-aware)
   */
  getCreditCardsForDropdown(companyId: string): Observable<Array<{key: string, value: string}>> {
    return this.paymentMethodsManager.getActiveCreditCards().pipe(
      map((cards: any[]) => 
        cards
          .filter(card => card.company === companyId)
          .filter(card => card.isActive)
          .map(card => ({
            key: card._id,
            value: card.shortName
          }))
      )
    );
  }

  /**
   * Get payment methods with their associated accounts/cards
   */
  getPaymentMethodsWithData(companyId: string): Observable<{
    bankTransfer: Array<{key: string, value: string}>,
    creditCard: Array<{key: string, value: string}>,
    debitCash: Array<{key: string, value: string}>,
    personalMoney: Array<{key: string, value: string}>
  }> {
    return this.paymentMethodsManager.loadAllPaymentMethods(companyId).pipe(
      map(({bankAccounts, creditCards}) => {
        const bankTransfer = bankAccounts
          .filter((account: any) => !account.isDebit)
          .filter((account: any) => !['DEBIT', 'CASH', 'TEMP'].includes(account.bankName?.toUpperCase()))
          .filter((account: any) => !['DEBIT', 'CASH', 'TEMP'].includes(account.branchName?.toUpperCase()))
          .map((account: any) => ({ key: account._id, value: account.shortName }));

        const creditCard = creditCards
          .filter((card: any) => card.isActive)
          .map((card: any) => ({ key: card._id, value: card.shortName }));

        const debitCash = bankAccounts
          .filter((account: any) => account.isDebit)
          .map((account: any) => ({ key: account._id, value: account.shortName }));

        const personalMoney = [{ key: 'personal', value: 'Personal Money' }];

        return {
          bankTransfer,
          creditCard,
          debitCash,
          personalMoney
        };
      })
    );
  }

  // Delegate other methods to the original EnumService
  getPaymentMethodOptions(): Array<string> {
    return this.enumService.getPaymentMethodOptions();
  }

  getCostTypeOptions(): Array<string> {
    return this.enumService.getCostTypeOptions();
  }

  getCostStatuseOptions(): Array<string> {
    return this.enumService.getCostStatuseOptions();
  }

  getAccountingCategoryOptions(): Array<string> {
    return this.enumService.getAccountingCategoryOptions();
  }

  getAssignToOptions(companyId: string): Promise<Array<{ id: string; fullName: string }>> {
    return this.enumService.getAssignToOptions(companyId) as unknown as Promise<Array<{ id: string; fullName: string }>>;
  }

  getCurrencyOptionsDetailed(): Array<any> {
    return this.enumService.getCurrencyOptionsDetailed();
  }

  getSubcategoryOptions(): Array<{ category: string; options: Array<string> }> {
    return this.enumService.getSubcategoryOptions();
  }

  getUsers(): Array<string> {
    return this.enumService.getUsers();
  }

  getAssignToValues(): Array<string> {
    return this.enumService.getUsers();
  }
}
